import {getLinkExpiration} from '@/services/sso/ssoExpiration';

export type ScheduleSsoInvalidationParams = {
    expirationDate: number | string | undefined;
    onExpire: () => void;
};

export const SSO_REFRESH_BUFFER_MS = 10_000;

/**
 * Schedules SSO invalidation slightly before expiry and a hard-expiry fallback.
 * Returns a disposer that clears all scheduled timeouts.
 */
export const scheduleSsoInvalidation = ({expirationDate, onExpire}: ScheduleSsoInvalidationParams): (() => void) => {
    if (expirationDate === undefined || expirationDate === '') {
        return () => {};
    }

    const expirationMs = typeof expirationDate === 'string' ? Number(expirationDate) : expirationDate;

    if (Number.isNaN(expirationMs)) {
        return () => {};
    }

    const {expiredInMs, isExpired} = getLinkExpiration(expirationMs);
    const preExpiryTimeoutMs = isExpired ? 0 : Math.max(0, expiredInMs - SSO_REFRESH_BUFFER_MS);
    const fallbackTimeoutMs = isExpired ? 0 : expiredInMs;
    const timeoutIds: number[] = [];

    const safeOnExpire = () => {
        try {
            onExpire();
        } catch {
            // Keep timeout callback resilient; fallback timer can still trigger later.
        }
    };

    timeoutIds.push(window.setTimeout(safeOnExpire, preExpiryTimeoutMs));

    if (!isExpired && fallbackTimeoutMs > preExpiryTimeoutMs) {
        timeoutIds.push(window.setTimeout(safeOnExpire, fallbackTimeoutMs));
    }

    return () => timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
};
